/**
 * /health — public liveness page for uptime monitors.
 *
 * Reports the build and a real round-trip to the configured Supabase
 * project's public auth health endpoint (no session or RLS involved, so a
 * failure means the API is unreachable, not merely empty).
 */
import { useEffect, useState } from 'react';

interface HealthReport {
  status: 'ok' | 'degraded' | 'checking';
  portal: 'estimator';
  timestamp: string;
  supabase: {
    configured: boolean;
    reachable: boolean | null;
    latency_ms: number | null;
    http_status: number | null;
    error: string | null;
  };
}

const CHECK_TIMEOUT_MS = 5_000;

async function checkSupabase(): Promise<HealthReport['supabase']> {
  const url = import.meta.env.VITE_SUPABASE_URL;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
  if (!url || !anonKey) {
    return { configured: false, reachable: null, latency_ms: null, http_status: null, error: 'VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY not set' };
  }
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), CHECK_TIMEOUT_MS);
  const started = performance.now();
  try {
    const res = await fetch(`${url.replace(/\/$/, '')}/auth/v1/health`, {
      headers: { apikey: anonKey },
      signal: controller.signal,
    });
    const latency = Math.round(performance.now() - started);
    return {
      configured: true,
      reachable: res.ok,
      latency_ms: latency,
      http_status: res.status,
      error: res.ok ? null : `HTTP ${res.status}`,
    };
  } catch (err) {
    const latency = Math.round(performance.now() - started);
    const message =
      err instanceof DOMException && err.name === 'AbortError'
        ? `No response within ${CHECK_TIMEOUT_MS} ms`
        : err instanceof Error
          ? err.message
          : 'Request failed';
    return { configured: true, reachable: false, latency_ms: latency, http_status: null, error: message };
  } finally {
    window.clearTimeout(timer);
  }
}

export function HealthPage() {
  const [report, setReport] = useState<HealthReport>({
    status: 'checking',
    portal: 'estimator',
    timestamp: new Date().toISOString(),
    supabase: { configured: Boolean(import.meta.env.VITE_SUPABASE_URL), reachable: null, latency_ms: null, http_status: null, error: null },
  });

  useEffect(() => {
    let cancelled = false;
    checkSupabase().then((supabase) => {
      if (cancelled) return;
      setReport({
        status: supabase.reachable ? 'ok' : 'degraded',
        portal: 'estimator',
        timestamp: new Date().toISOString(),
        supabase,
      });
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <main className="p-4">
      <h1 className="sr-only">Estimator health</h1>
      <pre
        className="text-sm font-mono text-text whitespace-pre-wrap break-words"
        data-health-status={report.status}
        aria-live="polite"
      >
        {JSON.stringify(report, null, 2)}
      </pre>
    </main>
  );
}
